/**
 * Where a planet dot may sit on the Visual Universe wheel.
 *
 * The wheel is a South-Indian square chart: an outer rectangle crossed by both diagonals and by
 * the four edges of an inscribed diamond, cutting it into twelve cells, one per sign. A dot marks
 * that a sign's house is occupied, and it must land inside that sign's own cell — a dot a few
 * pixels over a divider reads as belonging to the neighbour, which is the one thing it must not say.
 *
 * Extracted from the dialog because it is pure geometry over the sign coordinate table and is
 * worth testing directly; the component renders it and does not decide it.
 */

/**
 * The lines bounding a cell: the two diagonals, the four diamond edges, and the outer border.
 *
 * Which side of each line a point falls on identifies the cell containing it. That is enough to
 * answer the only question asked here — are these two points in the SAME cell — without having to
 * enumerate twelve polygons, which the chart's own `houses` array does not provide (it is
 * decorative fill and does not map one-to-one onto the signs).
 *
 * <p>The four border edges are in the set because these are infinite lines, not segments: without
 * them a point off the right-hand edge of the chart still falls on the home side of all six
 * interior dividers and is declared inside. Measured on a live chart, the Sagittarius and Aquarius
 * dots landed at x=2968 on a canvas 2916 wide — outside the drawing altogether, and reported as
 * correctly placed. An interior point is on the same side of all four borders as every other
 * interior point, so adding them changes nothing except catching that.
 *
 * @param {number} width  chart width in user units
 * @param {number} height chart height in user units
 * @returns {Array<[number, number, number, number]>} lines as [x1, y1, x2, y2]
 */
export const cellDividers = (width, height) => [
  [0, 0, width, height],
  [width, 0, 0, height],
  [width / 2, 0, width, height / 2],
  [width, height / 2, width / 2, height],
  [width / 2, height, 0, height / 2],
  [0, height / 2, width / 2, 0],
  [0, 0, width, 0],
  [width, 0, width, height],
  [width, height, 0, height],
  [0, height, 0, 0],
];

/** Which side of a directed line a point falls on: -1, 0 or +1. */
export const sideOf = (px, py, [x1, y1, x2, y2]) =>
  Math.sign((x2 - x1) * (py - y1) - (y2 - y1) * (px - x1));

/**
 * An opaque identifier for the cell containing a point — equal for two points in the same cell.
 *
 * It is a sign vector, not a cell number: nothing here needs to name the cell, only to compare two.
 */
export const cellKeyFor = (dividers) => (px, py) =>
  dividers.map(line => sideOf(px, py, line)).join(',');

/**
 * The cell a sign occupies, taken from the CENTROID of its text baselines.
 *
 * <p>Not from the topmost baseline, which is the tempting choice and is wrong for Scorpio: its
 * lines run parallel to a diamond edge and hug it, so the topmost one sits on the far side of that
 * divider from the other three. Using it as the home cell declares the dot out of bounds wherever
 * it is put, and the containment walk below then retreats the full width of the cell and still
 * fails. The centroid is what the sign's other coordinates agree on.
 *
 * @param {Array<{x: number, y: number}>} coords the sign's text baseline origins
 */
export const homeCellFor = (coords, cellKey) => {
  const centroid = coords.reduce(
    (acc, p) => ({ x: acc.x + p.x / coords.length, y: acc.y + p.y / coords.length }),
    { x: 0, y: 0 },
  );
  return cellKey(centroid.x, centroid.y);
};

/** How far past the dot's centre its edge reaches; the containment test uses the edge. */
export const DOT_CELL_MARGIN = 26;

const STEP = 16;

/**
 * The dot's x, pulled back from the column position until it sits inside the sign's own cell.
 *
 * <p>Dots are aligned per column so a column reads as one vertical line — but a column's width is
 * set by its WIDEST planet list, which pushes the dot of a sign holding a short list past its own
 * boundary and into a neighbour. Alignment is the goal; leaving the cell is not an acceptable
 * price for it, so `preferredX` is a starting point and containment wins where the two conflict.
 *
 * <p>Stepped rather than solved: which boundary a cell runs into depends on which cell it is, and
 * stepping asks the containment test itself rather than restating the geometry twelve times.
 * Sixteen units a step across a chart ~2900 wide is a handful of iterations.
 *
 * @returns {number} an x in [anchorX, preferredX]
 */
export const containDotX = (preferredX, anchorX, anchorY, home, cellKey) => {
  let dotX = preferredX;
  // The margin is tested, not merely applied: a dot whose CENTRE is inside but whose edge crosses
  // the divider still reads as spilling out of its cell.
  while (dotX > anchorX && cellKey(dotX + DOT_CELL_MARGIN, anchorY) !== home) {
    dotX -= STEP;
  }
  return Math.max(dotX, anchorX);
};

/**
 * The size ChartSVG draws at, in user units.
 *
 * The coordinate table below is authored against these numbers rather than in fractions, so the
 * two travel together — a dimension changed without the table would move every divider out from
 * under the text it is supposed to separate.
 */
export const CHART_WIDTH = 2916;
export const CHART_HEIGHT = 1026;

// --- BEGIN SIGN TEXT LINE COORDINATES ---
// Each subarray: [ {x, y}, ... ] for sign 0 (Aries) to sign 11 (Pisces)
export const SIGN_LINE_COORDS = [
  [ {x:930, y:265}, {x:1030, y:305}, {x:1030, y:225}, {x:1130, y:345}, {x:1130, y:185} ], // Aries
  [ {x:205, y:40}, {x:290, y:75}, {x:390, y:110}, {x:490, y:145} ], // Taurus
  [ {x:20, y:265}, {x:20, y:305}, {x:20, y:345}, {x:20, y:225}, {x:20, y:180} ], // Gemini
  [ {x:200, y:510}, {x:300, y:550}, {x:300, y:470}, {x:400, y:590}, {x:400, y:430} ], // Cancer
  [ {x:20, y:755}, {x:20, y:795}, {x:20, y:835}, {x:20, y:715}, {x:20, y:670} ], // Leo
  [ {x:180, y:980}, {x:265, y:945}, {x:365, y:910}, {x:465, y:875} ], // Virgo
  [ {x:930, y:760}, {x:1030, y:800}, {x:1030, y:720}, {x:1130, y:840}, {x:1130, y:680} ], // Libra
  [ {x:1620, y:980}, {x:1705, y:945}, {x:1790, y:910}, {x:1875, y:875} ], // Scorpio
  [ {x:2300, y:755}, {x:2385, y:795}, {x:2470, y:835}, {x:2385, y:715}, {x:2470, y:670} ], // Sagittarius
  [ {x:1710, y:460}, {x:1650, y:500}, {x:1590, y:540}, {x:1530, y:580}, {x:1470, y:620} ], // Capricorn
  [ {x:2300, y:265}, {x:2385, y:305}, {x:2470, y:345}, {x:2385, y:225}, {x:2470, y:180} ], // Aquarius
  [ {x:1645, y:40}, {x:1730, y:75}, {x:1830, y:110}, {x:1930, y:140} ] // Pisces
];
// --- END SIGN TEXT LINE COORDINATES ---
